import {useEffect, useState} from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    TextField
} from '@mui/material'
import {Button} from '@origamirisk/origami-storybook'
import axios from 'axios'
import {AccountMeta, PolicySet} from '../utils/types'

interface ResetVersionDataDialogProps {
    isOpen: boolean
    onClose: () => void
    onResetComplete: () => void
}

export function ResetVersionDataDialog({
    isOpen,
    onClose,
    onResetComplete
}: ResetVersionDataDialogProps) {
    const [loading, setLoading] = useState(false)
    const [metaLoading, setMetaLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [accountMeta, setAccountMeta] = useState<AccountMeta | null>(null)
    const [selectedPolicySet, setSelectedPolicySet] = useState<PolicySet | null>(null)

    useEffect(() => {
        if (!isOpen) return

        const fetchAccountMeta = async () => {
            setMetaLoading(true)
            setAccountMeta(null)
            setError(null)
            try {
                const {data} = await axios.get('/api/admin/account/meta')
                setAccountMeta(data)
            } catch (e: any) {
                console.error('Failed to fetch account metadata:', e)
                setError(e.response?.data || e.message || 'Failed to load Rating Programs')
            } finally {
                setMetaLoading(false)
            }
        }

        fetchAccountMeta()
    }, [isOpen])

    const handleClose = () => {
        setSelectedPolicySet(null)
        setError(null)
        onClose()
    }

    const handleReset = async () => {
        if (!selectedPolicySet) return

        setLoading(true)
        setError(null)

        try {
            await axios.post('/api/admin/versions/delete', {policySetId: Number(selectedPolicySet.id)})

            onResetComplete()
            handleClose()
        } catch (e: any) {
            console.error('Failed to reset version data:', e)
            setError(e.response?.data || e.message || 'Failed to reset version data')
        } finally {
            setLoading(false)
        }
    }

    const sortedPolicySets = [...(accountMeta?.policySets ?? [])].sort((a, b) => a.name.localeCompare(b.name))

    return (
        <Dialog
            open={isOpen}
            onClose={loading ? undefined : handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Reset Version Data</DialogTitle>
            <DialogContent>
                <Alert severity="error" sx={{mb: 2}}>
                    <strong>⚠️ WARNING:</strong> This operation cannot be undone!
                </Alert>
                <DialogContentText>
                    Resetting version data for a Rating Program will permanently delete:
                </DialogContentText>
                <Box component="ul" sx={{mt: 1, mb: 2}}>
                    <li>All version records for that Rating Program from the database</li>
                    <li>All associated S3 files for that Rating Program</li>
                </Box>

                <Autocomplete
                    size="small"
                    loading={metaLoading}
                    disabled={loading}
                    options={sortedPolicySets}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedPolicySet}
                    onChange={(_, newValue) => setSelectedPolicySet(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Rating Program"
                            required
                            error={!selectedPolicySet}
                            helperText={!selectedPolicySet ? 'Rating Program is required' : ' '}
                        />
                    )}
                />

                {error && (
                    <Alert severity="error" sx={{mt: 2}}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, width: '100%'}}>
                    <Button
                        onClick={handleReset}
                        disabled={loading || !selectedPolicySet}
                        color="error"
                        variant="contained"
                        size="md"
                        fullWidth
                    >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Reset Rating Program'
                        )}
                    </Button>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        color="secondary"
                        variant="text"
                        size="md"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}
